/**
 * Task 17: Vertical Collapsible Sidebar
 * SPES brand palette: Navy #1e3a5f, Gold #f59e0b, Teal #14b8a6
 */

import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Bell, 
  Upload, 
  FileText, 
  FileDown, 
  Settings as SettingsIcon,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Shield,  // Task 26: Admin dashboard icon
  UserCog,  // Task 26: Manager dashboard icon
  Calendar  // Task 36: My Follow-Ups icon
} from 'lucide-react';
import NotificationBell from './NotificationBell';  // Task 21
import { notifyAuthStateChanged } from '../utils/authSession';

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  
  useEffect(() => {
    // Get user info from localStorage
    const userStr = localStorage.getItem('spes_user');
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setUser(userData);
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }, [location]);
  
  // Task 17: Role-based navigation items
  const allNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['all'] },
    { path: '/admin-dashboard', icon: Shield, label: 'Admin Dashboard', roles: ['admin'] },  // Task 26: Admin user management
    { path: '/manager-dashboard', icon: UserCog, label: 'Manager Dashboard', roles: ['manager', 'bm', 'am', 'tl'] },  // Task 26: Manager recruiter creation
    { path: '/candidates', icon: Users, label: 'Candidates', roles: ['all'] },
    { path: '/my-followups', icon: Calendar, label: 'My Follow-Ups', roles: ['all'] },  // Task 36: Follow-Up Engine
    { path: '/follow-ups', icon: Bell, label: 'Follow-Ups', roles: ['all'] },
    { path: '/upload', icon: Upload, label: 'Upload', roles: ['all'] },  // Task 19 Fix: Recruiters need to upload candidates
    { path: '/jd-upload', icon: FileText, label: 'Job Descriptions', roles: ['all'] },
    { path: '/reports', icon: FileDown, label: 'Reports', roles: ['admin', 'bm', 'am', 'tl'] },
    { path: '/settings', icon: SettingsIcon, label: 'Settings', roles: ['all'] },  // Task 21: User settings
  ];
  
  // Filter nav items based on user role
  const navItems = allNavItems.filter(item => {
    if (item.roles.includes('all')) return true;
    if (!user) return false;
    return item.roles.includes(user.role);
  });
  
  const handleLogout = () => {
    localStorage.removeItem('spes_token');
    localStorage.removeItem('spes_role');
    localStorage.removeItem('spes_user');
    notifyAuthStateChanged();
    console.log('✅ Logged out successfully');
    navigate('/login');
  };
  
  const getRoleDisplay = (role) => {
    const roleMap = {
      'admin': 'SUPERADMIN',
      'bm': 'BRANCH MGR',
      'am': 'ACCOUNT MGR',
      'tl': 'TEAM LEAD',
      'recruiter': 'RECRUITER'
    };
    return roleMap[role] || role.toUpperCase();
  };
  
  return (
    <div 
      className={`fixed left-0 top-0 h-full bg-[#1e3a5f] border-r border-teal/30 shadow-xl transition-all duration-300 ease-in-out flex flex-col z-50
        ${collapsed ? 'w-20' : 'w-64'}`}
    >
      {/* Header */}
      <div className="p-4 border-b border-teal/30 flex items-center justify-between">
        <Link 
          to="/dashboard" 
          className={`font-bold text-[#f59e0b] hover:text-yellow-400 transition-colors duration-200
            ${collapsed ? 'text-lg' : 'text-xl'}`}
        >
          {collapsed ? 'SP' : 'Spes Recruiter'}
        </Link>
        
        <div className="flex items-center space-x-1">
          {/* Task 21: Notification Bell */}
          {!collapsed && <NotificationBell />}
          
          {/* Collapse Toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded-md hover:bg-teal/20 text-textLight/60 hover:text-teal transition-all duration-200"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>
      </div>
      
      {/* Navigation Items */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {navItems.map(({ path, icon: Icon, label }) => {
          const isActive = location.pathname === path;
          
          return (
            <Link
              key={path}
              to={path}
              className={`flex items-center space-x-3 px-3 py-3 rounded-lg mb-1 transition-all duration-200 group relative
                ${isActive 
                  ? 'bg-gradient-to-r from-teal/20 to-teal/10 text-[#14b8a6] border-r-4 border-[#14b8a6]' 
                  : 'text-textLight/70 hover:bg-navy-light/50 hover:text-[#14b8a6]'}`}
              title={collapsed ? label : ''}
            >
              <Icon size={20} className={`flex-shrink-0 ${isActive ? 'text-[#14b8a6]' : ''}`} />
              
              {!collapsed && (
                <span className={`text-sm font-medium transition-opacity duration-200
                  ${isActive ? 'text-[#14b8a6]' : ''}`}>
                  {label}
                </span>
              )}
              
              {/* Tooltip for collapsed state */}
              {collapsed && (
                <div className="absolute left-full ml-2 px-3 py-1.5 bg-navy text-textLight text-sm rounded-md
                  opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap
                  shadow-lg border border-teal/30">
                  {label}
                </div>
              )}
            </Link>
          );
        })}
      </nav>
      
      {/* Footer - User Info & Logout */}
      <div className="p-4 border-t border-teal/30 space-y-3">
        {/* User Info */}
        {user && !collapsed && (
          <div className="mb-3">
            <p className="text-xs text-textLight/60 mb-1">Welcome,</p>
            <p className="text-sm font-medium text-[#14b8a6] truncate">{user.name}</p>
            <div className="mt-2 px-2 py-1 bg-[#f59e0b]/10 border border-[#f59e0b]/30 rounded-md inline-block">
              <span className="text-xs font-semibold text-[#f59e0b] uppercase tracking-wide">
                {getRoleDisplay(user.role)}
              </span>
            </div>
          </div>
        )}
        
        {/* Collapsed User Badge */}
        {user && collapsed && (
          <div className="flex justify-center mb-2">
            <div className="w-10 h-10 rounded-full bg-[#f59e0b]/20 border-2 border-[#f59e0b]/50 flex items-center justify-center">
              <span className="text-xs font-bold text-[#f59e0b]">
                {user.name.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
        )}
        
        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className={`w-full flex items-center space-x-2 px-3 py-2.5 rounded-lg text-sm font-medium
            text-textLight/70 hover:bg-red-500/10 hover:text-red-400 transition-all duration-200
            ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Logout' : ''}
        >
          <LogOut size={18} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
}
